import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { iosNativeContent, iosNativePageRoot } from '../styles/iosNativePageCss';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Sparkles, ArrowLeft, ChefHat, Save } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { useToast } from '../components/ui/Toast';
import { useAWSAuth as useAuth } from '../contexts/AWSAuthContext';
import type { DIYMask, ScanAnalysis } from '../services/scanService';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  padding: 2rem 1rem 4rem;

  ${iosNativePageRoot}
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;

  ${iosNativeContent}
`;

const Header = styled.div`
  margin-bottom: 2rem;
  animation: slideUp 0.5s ease-out;
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.secondary};
  margin-top: 0.5rem;
`;

const BackButton = styled(Button)`
  margin-bottom: 1.5rem;
`;

const MasksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const MaskCard = styled(Card)`
  padding: 1.5rem;
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
`;

const MaskHeader = styled.div`
  display: flex;
  align-items: start;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const MaskEmoji = styled.span`
  font-size: 2.5rem;
  line-height: 1;
`;

const MaskTitle = styled.h3`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin: 0 0 0.5rem 0;
  flex: 1;
`;

const MaskDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin: 0 0 1rem 0;
  line-height: 1.6;
`;

const BenefitsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const RecipeSection = styled.details`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${theme.colors.border.light};
`;

const RecipeSummary = styled.summary`
  cursor: pointer;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.primary[600]};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    color: ${theme.colors.primary[700]};
  }
`;

const RecipeContent = styled.div`
  margin-top: 1rem;
  padding-left: 1rem;
`;

const RecipeSectionTitle = styled.strong`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
  display: block;
  margin-bottom: 0.5rem;
`;

const IngredientsList = styled.ul`
  margin: 0.5rem 0 1rem 0;
  padding-left: 1.5rem;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.8;
`;

const InstructionsList = styled.ol`
  margin: 0.5rem 0 0 0;
  padding-left: 1.5rem;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.8;
`;

const StepItem = styled.li`
  margin-bottom: 0.5rem;
`;

const RecipeInfo = styled.p`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.tertiary};
  margin: 0.5rem 0;
`;

const ScienceNote = styled.p`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  font-style: italic;
  margin: 0.5rem 0 0 0;
  line-height: 1.6;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${theme.colors.text.secondary};
`;

const EmptyStateTitle = styled.h2`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.base};
  margin-bottom: 1.5rem;
`;

const DIYMasksDetailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const auth = useAuth();
  const userProfile = auth?.userProfile;
  const { diyMasks, scanId, scanCustomName, totalMasks, createdAt, scanAnalysis } = (location.state as { 
    diyMasks?: DIYMask[]; 
    scanId?: string; 
    scanCustomName?: string;
    totalMasks?: number;
    createdAt?: string;
    scanAnalysis?: ScanAnalysis;
  }) || {};

  const [masks, setMasks] = useState<DIYMask[]>([]);

  useEffect(() => {
    // If masks are passed, use them; otherwise generate them
    if (diyMasks && diyMasks.length > 0) {
      setMasks(diyMasks);
    } else if (scanAnalysis) {
      // Generate masks if not passed
      import('../utils/diyMaskGenerator').then(({ generateAdditionalDIYMasks }) => {
        const generatedMasks = generateAdditionalDIYMasks(
          scanAnalysis.skinHealthIndex,
          scanAnalysis.acneAnalysis?.overallSeverity || 'mild',
          scanAnalysis.acneAnalysis?.estimatedPH ?? undefined,
          scanAnalysis.acneAnalysis?.acneType?.inflammatory === 'moderate' || 
          scanAnalysis.acneAnalysis?.acneType?.inflammatory === 'pronounced'
        );
        setMasks(generatedMasks);
      });
    }
  }, [diyMasks, scanAnalysis]);

  const filteredMasks = masks;

  if (!filteredMasks || filteredMasks.length === 0) {
    return (
      <PageContainer>
        <Container>
          <BackButton
            variant="outline"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={16} />
            Back
          </BackButton>
          <EmptyState>
            <EmptyStateTitle>No DIY Masks Found</EmptyStateTitle>
            <EmptyStateText>
              We couldn't find any DIY masks. Please go back and try again.
            </EmptyStateText>
            <Button
              variant="primary"
              onClick={() => navigate('/diy-masks')}
            >
              Go to DIY Masks Page
            </Button>
          </EmptyState>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <BackButton
          variant="outline"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={16} />
          Back
        </BackButton>

        <Header>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
            <div>
              <Title>
                <Sparkles size={32} color={theme.colors.primary[600]} />
                More Recommended DIY Face Masks
              </Title>
              <Subtitle>
                {totalMasks ? `Showing ${filteredMasks.length} additional personalized masks (${totalMasks} total recommended)` : `Complete list of ${filteredMasks.length} personalized face masks tailored to your skin analysis`}
                {createdAt && (
                  <span style={{ display: 'block', marginTop: '0.5rem', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.tertiary }}>
                    Generated for scan on {new Date(createdAt).toLocaleDateString()}
                  </span>
                )}
              </Subtitle>
            </div>
            {filteredMasks.length > 0 && scanId && userProfile?.uid && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (userProfile?.uid && scanId) {
                    const savedMasks = JSON.parse(localStorage.getItem(`savedDIYMasks_${userProfile.uid}`) || '[]');
                    filteredMasks.forEach(mask => {
                      if (!savedMasks.find((m: any) => m.id === mask.id && m.scanId === scanId)) {
                        savedMasks.push({
                          ...mask,
                          scanId: scanId,
                          scanCustomName: scanCustomName,
                          savedAt: new Date().toISOString(),
                        });
                      }
                    });
                    localStorage.setItem(`savedDIYMasks_${userProfile.uid}`, JSON.stringify(savedMasks));
                    showToast(`${filteredMasks.length} additional masks saved to DIY Masks tab!`, 'success');
                  }
                }}
              >
                <Save size={16} />
                Save All Masks
              </Button>
            )}
          </div>
        </Header>

        <MasksGrid>
          {filteredMasks.map((mask) => (
            <MaskCard key={mask.id}>
              <MaskHeader>
                <MaskEmoji>{mask.emoji}</MaskEmoji>
                <div style={{ flex: 1 }}>
                  <MaskTitle>{mask.title}</MaskTitle>
                  <MaskDescription>{mask.description}</MaskDescription>
                </div>
              </MaskHeader>

              <BenefitsContainer>
                {mask.benefits.map((benefit, i) => (
                  <Badge key={i} variant="info" size="sm">
                    {benefit}
                  </Badge>
                ))}
              </BenefitsContainer>

              <RecipeSection>
                <RecipeSummary>
                  <ChefHat size={16} />
                  View Recipe & Instructions
                </RecipeSummary>
                <RecipeContent>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <RecipeSectionTitle>Ingredients:</RecipeSectionTitle>
                    <IngredientsList>
                      {mask.ingredients.map((ing, i) => (
                        <li key={i}>{ing}</li>
                      ))}
                    </IngredientsList>
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <RecipeSectionTitle>Instructions:</RecipeSectionTitle>
                    <InstructionsList>
                      {mask.instructions.map((step, i) => (
                        <StepItem key={i}>{step}</StepItem>
                      ))}
                    </InstructionsList>
                  </div>
                  <RecipeInfo>
                    <strong>Duration:</strong> {mask.duration}
                  </RecipeInfo>
                  <ScienceNote>{mask.science}</ScienceNote>
                </RecipeContent>
              </RecipeSection>
            </MaskCard>
          ))}
        </MasksGrid>
      </Container>
    </PageContainer>
  );
};

export default DIYMasksDetailPage;

